import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Printer, ArrowRight } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import { labResultsAPI, patientPortalAPI } from '../../services/api';
import { useTenantBranding } from '../../context/TenantBrandingContext';
import toast from 'react-hot-toast';

const CHART_COLORS = ['#2563eb', '#16a34a', '#ea580c', '#7c3aed', '#dc2626'];

const MedicalReport = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenantBranding();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const printRef = useRef();

  const isPatientPortal = location.pathname.startsWith('/patient/');
  const isContractorPortal = location.pathname.startsWith('/contractor/');

  useEffect(() => {
    const fetchData = async () => {
      try {
        let response;

        if (isPatientPortal) {
          response = await patientPortalAPI.getBookingReport(bookingId);
        } else {
          response = await labResultsAPI.getBookingResults(bookingId);
        }
        setData(response.data?.data);
      } catch (err) {
        console.error('Error loading report:', err);
        const msg = err?.response?.data?.message || 'حدث خطأ في تحميل النتائج';
        toast.error(msg);
        if (isPatientPortal) {
          navigate('/patient/results');
        } else if (isContractorPortal) {
          navigate('/contractor/dashboard');
        } else {
          navigate('/bookings/approve');
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [bookingId, navigate, isPatientPortal, isContractorPortal]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    if (isPatientPortal) {
      navigate('/patient/results');
    } else if (isContractorPortal) {
      navigate('/contractor/dashboard');
    } else {
      navigate(-1);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const groupTestsByCategory = (tests) => {
    const grouped = {};
    tests?.forEach(test => {
      // Exclude serial_with_chart tests from grouped categories
      const layout = test.lab_test_id?.print_layout_style || 'standard_vertical';
      if (layout === 'serial_with_chart' && test.result?.serial_results?.length > 0) return;

      const category = test.lab_test_id?.category_id?.name_en ||
        test.lab_test_id?.category_id?.name_ar ||
        test.lab_test_id?.department ||
        'General';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(test);
    });
    return grouped;
  };

  const getSerialChartTests = (tests) => {
    return (tests || []).filter(test => {
      const layout = test.lab_test_id?.print_layout_style || 'standard_vertical';
      return layout === 'serial_with_chart' && test.result?.serial_results?.length > 0;
    });
  };

  // ─── Rendering helpers ───

  const getResultStatus = (comp) => {
    const value = comp.value_numeric ?? comp.value_text ?? null;
    const numValue = parseFloat(value);
    const lowVal = comp.reference_low ?? comp.low_value;
    const highVal = comp.reference_high ?? comp.high_value;
    let statusText = '';
    let statusColor = '#333';

    if (!isNaN(numValue) && lowVal != null && highVal != null) {
      if (numValue < lowVal) { statusText = 'Low'; statusColor = '#dc2626'; }
      else if (numValue > highVal) { statusText = 'High'; statusColor = '#dc2626'; }
      else { statusText = 'Normal'; statusColor = '#16a34a'; }
    } else if (comp.abnormal_flag) {
      if (comp.abnormal_flag === 'L') { statusText = 'Low'; statusColor = '#dc2626'; }
      else if (comp.abnormal_flag === 'H') { statusText = 'High'; statusColor = '#dc2626'; }
      else if (comp.abnormal_flag === 'N') { statusText = 'Normal'; statusColor = '#16a34a'; }
    }

    return { statusText, statusColor };
  };

  const getReferenceText = (comp) => {
    const lowVal = comp.reference_low ?? comp.low_value;
    const highVal = comp.reference_high ?? comp.high_value;
    if (lowVal != null && highVal != null) return `${lowVal} - ${highVal}`;
    if (comp.normal_text) return comp.normal_text;
    return '-';
  };

  // ─── Standard test rows ───

  const renderStandardRows = (test, testIdx) => {
    const components = test.result?.components || [];
    const testName = test.lab_test_id?.name_en || '-';
    const printLayout = test.lab_test_id?.print_layout_style || 'standard_vertical';

    if (components.length === 0) {
      return (
        <tr key={`empty-${testIdx}`}>
          <td style={styles.cellLeft}>{testName}</td>
          <td style={{ ...styles.cellCenter, color: '#999', fontStyle: 'italic' }}>قيد الانتظار</td>
          <td style={styles.cellCenter}>-</td>
          <td style={styles.cellCenter}>-</td>
        </tr>
      );
    }

    // Grid layout for tests with many components
    if (printLayout !== 'standard_vertical' && components.length > 4) {
      const cols = printLayout === 'three_column_grid' ? 3 : 2;
      return (
        <tr key={`grid-${testIdx}`}>
          <td colSpan="4" style={{ padding: '10px 15px', borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: '8px' }}>
              {components.map((comp, ci) => {
                const { statusColor } = getResultStatus(comp);
                return (
                  <div key={ci} style={{ border: '1px solid #eee', borderRadius: 6, padding: '6px 8px' }}>
                    <div style={{ fontSize: 11, color: '#555' }}>{comp.component_name || testName}</div>
                    <div style={{ fontWeight: 'bold', fontSize: 13, color: statusColor }}>
                      {comp.value_numeric ?? comp.value_text ?? '-'}
                    </div>
                    {comp.unit && <div style={{ fontSize: 10, color: '#888' }}>{comp.unit}</div>}
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      );
    }

    return components.map((comp, ci) => {
      const value = comp.value_numeric ?? comp.value_text ?? '-';
      const { statusText, statusColor } = getResultStatus(comp);
      const refText = getReferenceText(comp);

      return (
        <tr key={`${testIdx}-${ci}`} style={{ background: '#fff' }}>
          <td style={styles.cellLeft}>{comp.component_name || testName}</td>
          <td style={styles.cellCenter}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
              <span style={{ fontSize: '15px', fontWeight: 'bold', color: statusColor }}>{value}</span>
              {statusText && <span style={{ fontSize: '10px', color: statusColor, fontWeight: '500' }}>{statusText}</span>}
            </div>
          </td>
          <td style={{ ...styles.cellCenter, color: '#666', fontSize: '11px' }}>{comp.unit || '-'}</td>
          <td style={{ ...styles.cellCenter, fontWeight: '500', fontSize: '11px' }}>{refText}</td>
        </tr>
      );
    });
  };

  // ─── Serial table (no chart, e.g. GTT) ───

  const renderSerialTable = (test, testIdx) => {
    const serialResults = test.result?.serial_results || [];
    const baseComponents = serialResults[0]?.components || [];

    return (
      <tr key={`serial-${testIdx}`}>
        <td colSpan="4" style={{ padding: '10px 15px', borderBottom: '1px solid #eee' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', direction: 'ltr' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ddd' }}>Component</th>
                {serialResults.map((sr, i) => (
                  <th key={i} style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #ddd' }}>{sr.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {baseComponents.map((baseComp, ci) => (
                <tr key={ci}>
                  <td style={{ padding: '6px 8px', borderTop: '1px solid #eee' }}>{baseComp.component_name || '-'}</td>
                  {serialResults.map((sr, si) => {
                    const c = (sr.components || [])[ci] || {};
                    return (
                      <td key={si} style={{ padding: '6px 8px', borderTop: '1px solid #eee', textAlign: 'center' }}>
                        <div>{c.value_numeric ?? c.value_text ?? '-'}</div>
                        <div style={{ fontSize: 9, color: '#777' }}>
                          {sr.sample_collected_at ? new Date(sr.sample_collected_at).toLocaleTimeString('en-GB') : '-'}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    );
  };

  // ─── Culture rows ───

  const renderCultureRows = (test, testIdx) => {
    const cultureRows = test.result?.culture_grid_rows || [];
    return (
      <tr key={`culture-${testIdx}`}>
        <td colSpan="4" style={{ padding: '10px 15px', borderBottom: '1px solid #eee' }}>
          {cultureRows.map((row, i) => (
            <div key={i} style={{ marginBottom: 8, border: '1px solid #eee', borderRadius: 6, padding: 8 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{row.organism_name}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', fontSize: 11, padding: '2px 0', fontWeight: '600', borderBottom: '1px solid #ddd', marginBottom: 4 }}>
                <span>Antibiotic</span><span>Sensitivity</span><span>MIC</span>
              </div>
              {(row.antibiotics || []).map((ab, j) => (
                <div key={j} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', fontSize: 11, padding: '2px 0' }}>
                  <span>{ab.antibiotic_name}</span><span>{ab.sensitivity}</span><span>{ab.mic_value || '-'}</span>
                </div>
              ))}
            </div>
          ))}
        </td>
      </tr>
    );
  };

  // ─── Rich text rows ───

  const renderRichTextRows = (test, testIdx) => {
    const richHtml = test.result?.rich_text_html || '';
    const richImages = test.result?.rich_text_images || [];
    return (
      <tr key={`rich-${testIdx}`}>
        <td colSpan="4" style={{ padding: '10px 15px', borderBottom: '1px solid #eee' }}>
          <div dangerouslySetInnerHTML={{ __html: richHtml }} />
          {richImages.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 10 }}>
              {richImages.map((img, i) => (
                <img key={i} src={img.url} alt={img.caption || `img-${i}`} style={{ width: '100%', height: 90, objectFit: 'cover', border: '1px solid #ddd', borderRadius: 4 }} />
              ))}
            </div>
          )}
        </td>
      </tr>
    );
  };

  // ─── Determine which renderer to use for a test inside grouped table ───

  const renderTestContent = (test, testIdx) => {
    const serialResults = test.result?.serial_results || [];
    const cultureRows = test.result?.culture_grid_rows || [];
    const richHtml = test.result?.rich_text_html || null;

    if (serialResults.length > 0) {
      return renderSerialTable(test, testIdx);
    }
    if (cultureRows.length > 0) {
      return renderCultureRows(test, testIdx);
    }
    if (richHtml) {
      return renderRichTextRows(test, testIdx);
    }
    return renderStandardRows(test, testIdx);
  };

  // ─── Serial With Chart (standalone section) ───

  const renderSerialWithChart = (test, idx) => {
    const result = test.result;
    const components = result?.components || [];
    const serialResults = result?.serial_results || [];
    const testName = test.lab_test_id?.name_en || '-';
    const chartConfig = test.lab_test_id?.chart_config || {};
    const chartType = chartConfig.chart_type || 'line';
    const evaluation = result?.evaluation || null;

    // Build component info map from enriched serial results
    const compInfoMap = {};
    const firstInterval = serialResults[0]?.components || [];
    firstInterval.forEach(c => {
      const id = String(c.test_component_id);
      compInfoMap[id] = {
        name: c.component_name || '',
        unit: c.unit || '',
        result_type: c.result_type || '',
        reference_low: c.reference_low ?? null,
        reference_high: c.reference_high ?? null,
        normal_text: c.normal_text || '',
        critical_value: c.critical_value ?? null,
        critical_label: c.critical_label || ''
      };
    });

    // Fallback from regular components
    (components || []).forEach(c => {
      const id = String(c.test_component_id || c.component_id);
      if (!compInfoMap[id] || !compInfoMap[id].name) {
        compInfoMap[id] = {
          name: c.component_name || '',
          unit: c.unit || '',
          result_type: c.result_type || '',
          reference_low: c.reference_low ?? null,
          reference_high: c.reference_high ?? null,
          normal_text: c.normal_text || '',
          critical_value: c.critical_value ?? null,
          critical_label: c.critical_label || ''
        };
      }
    });

    // Step A: Separate info vs measurement components
    // Measurement = numeric + has reference range or critical threshold
    // Info = everything else (single_select, text, numeric without ranges like "Total Test Duration")
    const isMeasurement = (info) => {
      if (!info) return false;
      if (info.result_type === 'single_select' || info.result_type === 'text') return false;
      // Numeric but no reference range and no critical value = info component (e.g. Duration)
      if (info.reference_high == null && info.critical_value == null && !info.normal_text) return false;
      return true;
    };

    const infoComponents = firstInterval.filter(c => {
      const info = compInfoMap[String(c.test_component_id)];
      return !isMeasurement(info);
    });

    const numericCompIds = firstInterval
      .filter(c => {
        const info = compInfoMap[String(c.test_component_id)];
        if (!isMeasurement(info)) return false;
        return serialResults.some(sr =>
          (sr.components || []).some(sc => String(sc.test_component_id) === String(c.test_component_id) && sc.value_numeric != null)
        );
      })
      .map(c => String(c.test_component_id));

    // Build chart data
    const chartData = serialResults.map(sr => {
      const point = { name: sr.label || '-' };
      numericCompIds.forEach(compId => {
        const name = compInfoMap[compId]?.name || compId;
        const found = (sr.components || []).find(c => String(c.test_component_id) === compId);
        point[name] = found?.value_numeric != null ? found.value_numeric : null;
      });
      return point;
    });

    const componentNames = numericCompIds.map(id => compInfoMap[id]?.name || '-');

    // Critical thresholds
    const criticalThresholds = {};
    numericCompIds.forEach(compId => {
      const info = compInfoMap[compId];
      if (info?.critical_value != null) {
        criticalThresholds[info.name] = info.critical_value;
      }
    });
    // Also from serial result components directly
    if (serialResults.length > 0) {
      (serialResults[0].components || []).forEach(c => {
        if (c.critical_value != null && c.component_name && !criticalThresholds[c.component_name]) {
          criticalThresholds[c.component_name] = c.critical_value;
        }
      });
    }

    // Reference lines for chart (deduplicated by value)
    const uniqueThresholds = [];
    const seenVals = new Set();
    Object.entries(criticalThresholds).forEach(([name, val]) => {
      if (!seenVals.has(val)) {
        seenVals.add(val);
        uniqueThresholds.push({ value: val, label: `${val} ppm` });
      }
    });

    // Custom label that offsets vertically based on line index to avoid overlap
    const CustomValueLabel = ({ x, y, value, index: pointIndex, dataKey }) => {
      if (value == null) return null;
      const lineIndex = componentNames.indexOf(dataKey);
      // Check if other lines have the same value at this point
      const point = chartData[pointIndex];
      let offset = -12;
      if (point) {
        const sameValueCount = componentNames.filter(n => point[n] === value).length;
        if (sameValueCount > 1) {
          // Spread labels: first line goes up, second stays, third goes down
          const positionInGroup = componentNames.filter(n => point[n] === value).indexOf(dataKey);
          offset = -14 - (positionInGroup * 14);
        }
      }
      return (
        <text x={x} y={y + offset} textAnchor="middle" fill={CHART_COLORS[lineIndex >= 0 ? lineIndex % CHART_COLORS.length : 0]} fontSize={9} fontWeight="700">
          {value}
        </text>
      );
    };

    return (
      <div key={`chart-test-${idx}`} style={{ direction: 'ltr', marginBottom: '20px', pageBreakInside: 'avoid' }}>
        {/* 1. Test Title */}
        <div style={{ textAlign: 'center', padding: '16px 0 12px', borderBottom: '2px solid #2563eb', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', margin: 0 }}>{testName}</h2>
        </div>

        {/* 2. Test Information table */}
        {infoComponents.length > 0 && (
          <div style={{ margin: '0 0 16px 0', pageBreakInside: 'avoid' }}>
            <div style={{ background: '#2563eb', color: '#fff', padding: '6px 14px', fontSize: '12px', fontWeight: '600', borderRadius: '4px 4px 0 0' }}>
              Test Information
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db', borderTop: 'none', fontSize: '12px' }}>
              <tbody>
                {infoComponents.map((c, i) => {
                  const info = compInfoMap[String(c.test_component_id)];
                  const val = c.value_text || c.value_numeric || '-';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #e5e7eb', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ padding: '8px 14px', fontWeight: '500', color: '#374151', width: '40%' }}>{info?.name || '-'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right', color: '#111827' }}>
                        {val}{info?.unit ? ` ${info.unit}` : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 3. Component info boxes */}
        {numericCompIds.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', margin: '0 0 16px 0', justifyContent: 'center', flexWrap: 'wrap' }}>
            {numericCompIds.map((compId, ci) => {
              const info = compInfoMap[compId];
              const color = CHART_COLORS[ci % CHART_COLORS.length];
              return (
                <div key={ci} style={{
                  border: `2px solid ${color}`,
                  borderRadius: '8px',
                  padding: '8px 16px',
                  textAlign: 'center',
                  flex: 1,
                  maxWidth: '200px',
                  minWidth: '140px',
                  background: '#fff'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                    {info?.name || '-'}
                  </div>
                  {info?.critical_value != null && (
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                      Critical: <span style={{ fontWeight: '600', color }}>{'\u2265'} {info.critical_value} {info.unit || 'ppm'}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 4. Chart */}
        {chartData.length > 0 && numericCompIds.length > 0 && (
          <div style={{ margin: '0 0 16px 0', pageBreakInside: 'avoid' }}>
            <div style={{ background: '#2563eb', color: '#fff', padding: '6px 14px', fontSize: '12px', fontWeight: '600', borderRadius: '4px 4px 0 0' }}>
              Results Chart
            </div>
            <div style={{ border: '1px solid #d1d5db', borderTop: 'none', borderRadius: '0 0 4px 4px', padding: '12px 8px', background: '#fff' }}>
              {chartType === 'bar' ? (
                <BarChart width={640} height={340} data={chartData} margin={{ top: 20, right: 50, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#374151' }}
                    label={chartConfig.x_axis_label ? { value: chartConfig.x_axis_label, position: 'insideBottom', offset: -2, fontSize: 11, fill: '#6b7280' } : undefined} />
                  <YAxis tick={{ fontSize: 11, fill: '#374151' }}
                    label={chartConfig.y_axis_label ? { value: chartConfig.y_axis_label, angle: -90, position: 'insideLeft', offset: 4, fontSize: 11, fill: '#6b7280' } : undefined} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} align="center" verticalAlign="top" />
                  {uniqueThresholds.map((t, i) => (
                    <ReferenceLine key={`thresh-${i}`} y={t.value} stroke="#9ca3af" strokeDasharray="8 4" strokeWidth={1.5}
                      label={{ value: t.label, position: 'right', fontSize: 10, fill: '#6b7280', fontWeight: '500' }} />
                  ))}
                  {componentNames.map((name, ci) => (
                    <Bar key={name} dataKey={name} fill={CHART_COLORS[ci % CHART_COLORS.length]} label={<CustomValueLabel />} />
                  ))}
                </BarChart>
              ) : (
                <LineChart width={640} height={340} data={chartData} margin={{ top: 20, right: 50, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#374151' }}
                    label={chartConfig.x_axis_label ? { value: chartConfig.x_axis_label, position: 'insideBottom', offset: -2, fontSize: 11, fill: '#6b7280' } : undefined} />
                  <YAxis tick={{ fontSize: 11, fill: '#374151' }}
                    label={chartConfig.y_axis_label ? { value: chartConfig.y_axis_label, angle: -90, position: 'insideLeft', offset: 4, fontSize: 11, fill: '#6b7280' } : undefined} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} align="center" verticalAlign="top" />
                  {uniqueThresholds.map((t, i) => (
                    <ReferenceLine key={`thresh-${i}`} y={t.value} stroke="#9ca3af" strokeDasharray="8 4" strokeWidth={1.5}
                      label={{ value: t.label, position: 'right', fontSize: 10, fill: '#6b7280', fontWeight: '500' }} />
                  ))}
                  {componentNames.map((name, ci) => (
                    <Line key={name} type="monotone" dataKey={name} stroke={CHART_COLORS[ci % CHART_COLORS.length]}
                      strokeWidth={2.5} dot={{ r: 5, fill: CHART_COLORS[ci % CHART_COLORS.length], stroke: '#fff', strokeWidth: 2 }}
                      activeDot={{ r: 7 }} label={<CustomValueLabel />} />
                  ))}
                </LineChart>
              )}
            </div>
          </div>
        )}

        {/* 5. Data table */}
        {chartData.length > 0 && numericCompIds.length > 0 && (
          <div style={{ margin: '0 0 16px 0', pageBreakBefore: 'always' }}>
            <div style={{ background: '#2563eb', color: '#fff', padding: '6px 14px', fontSize: '12px', fontWeight: '600', borderRadius: '4px 4px 0 0' }}>
              Gas Measurements Over Time
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', tableLayout: 'fixed', border: '1px solid #d1d5db', borderTop: 'none' }}>
              <thead>
                <tr style={{ background: '#374151', color: '#fff' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'center', border: '1px solid #4b5563', fontSize: '11px' }}>Time (min)</th>
                  {numericCompIds.map((compId, ci) => {
                    const info = compInfoMap[compId];
                    return (
                      <th key={ci} style={{ padding: '8px 10px', textAlign: 'center', border: '1px solid #4b5563', fontSize: '11px' }}>
                        {info?.name || '-'}{info?.unit ? ` (${info.unit})` : ''}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {serialResults.map((sr, ri) => (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '8px 10px', textAlign: 'center', border: '1px solid #e5e7eb', fontWeight: '500', fontSize: '11px' }}>
                      {sr.label}
                    </td>
                    {numericCompIds.map((compId, ci) => {
                      const found = (sr.components || []).find(c => String(c.test_component_id) === compId);
                      const val = found?.value_numeric;
                      const threshold = compInfoMap[compId]?.critical_value;
                      const isExceeded = threshold != null && val != null && val >= threshold;
                      return (
                        <td key={ci} style={{
                          padding: '8px 10px', textAlign: 'center', border: '1px solid #e5e7eb',
                          fontWeight: isExceeded ? '700' : '400',
                          background: isExceeded ? '#fef3c7' : 'inherit',
                          color: isExceeded ? '#92400e' : '#111827',
                          fontSize: '11px'
                        }}>
                          {val != null ? val : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Normal Range footer */}
                <tr style={{ background: '#f0fdf4' }}>
                  <td style={{ padding: '8px 10px', textAlign: 'center', border: '1px solid #d1d5db', fontWeight: '600', fontSize: '11px', color: '#166534' }}>
                    Normal Range
                  </td>
                  {numericCompIds.map((compId, ci) => {
                    const info = compInfoMap[compId];
                    let rangeText = '-';
                    if (info?.normal_text) rangeText = info.normal_text;
                    else if (info?.critical_value != null) rangeText = `Less than ${info.critical_value} ${info.unit || 'ppm'}`;
                    else if (info?.reference_low != null && info?.reference_high != null) rangeText = `${info.reference_low} - ${info.reference_high} ${info.unit || ''}`;
                    return (
                      <td key={ci} style={{ padding: '8px 10px', textAlign: 'center', border: '1px solid #d1d5db', fontWeight: '500', fontSize: '10px', color: '#166534' }}>
                        {rangeText}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* 6. Evaluation */}
        {evaluation && (
          <div style={{ margin: '0 0 16px 0', pageBreakInside: 'avoid' }}>
            <div style={{ background: '#2563eb', color: '#fff', padding: '6px 14px', fontSize: '12px', fontWeight: '600', borderRadius: '4px 4px 0 0' }}>
              Result Evaluation
            </div>
            <div style={{ border: '1px solid #d1d5db', borderTop: 'none', borderRadius: '0 0 4px 4px', padding: '16px' }}>
              {evaluation.overall_result && (() => {
                const isPositive = /positive|abnormal|high/i.test(evaluation.overall_result);
                const borderColor = isPositive ? '#ea580c' : '#16a34a';
                const bgColor = isPositive ? '#fff7ed' : '#f0fdf4';
                const textColor = isPositive ? '#c2410c' : '#166534';
                return (
                  <div style={{
                    border: `3px solid ${borderColor}`, borderRadius: '8px', padding: '14px 24px',
                    textAlign: 'center', background: bgColor, marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: '500' }}>Overall Result</div>
                    <div style={{ fontSize: '17px', fontWeight: 'bold', color: textColor }}>{evaluation.overall_result}</div>
                  </div>
                );
              })()}
              {evaluation.evaluation_items?.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {evaluation.evaluation_items.map((item, i) => {
                    const isAbnormal = item.is_abnormal || false;
                    return (
                      <div key={i} style={{
                        border: `1px solid ${isAbnormal ? '#fdba74' : '#bbf7d0'}`,
                        borderRadius: '8px', padding: '10px 14px',
                        background: isAbnormal ? '#fff7ed' : '#f0fdf4'
                      }}>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>{item.label}</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: isAbnormal ? '#c2410c' : '#166534' }}>
                            {item.value ?? '-'}
                          </span>
                          {item.unit && <span style={{ fontSize: '11px', color: '#6b7280' }}>{item.unit}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Loading state ───

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-400 border-t-transparent"></div>
      </div>
    );
  }

  // ─── Data extraction ───

  const { booking, tests } = data || {};
  const customer = booking?.customer_id;
  const patientAge = calculateAge(customer?.date_of_birth);
  const patientGender = customer?.gender === 'Male' ? 'ذكر' : customer?.gender === 'Female' ? 'أنثى' : '-';
  const groupedTests = groupTestsByCategory(tests);
  const serialChartTests = getSerialChartTests(tests);
  const currentDate = formatDate(new Date());
  const reportTenant = data?.tenant || tenant;
  const logoUrl = reportTenant?.branding?.logoUrl;
  const labName = reportTenant?.branding?.labName || reportTenant?.nameAr || reportTenant?.name || '';

  // ─── Render ───

  return (
    <div className="min-h-screen bg-gray-200">
      {/* Print Controls */}
      <div className="print:hidden bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-[210mm] mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800">
            <ArrowRight className="w-5 h-5" />
            <span>رجوع</span>
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">
            <Printer className="w-5 h-5" />
            <span>طباعة التقرير</span>
          </button>
        </div>
      </div>

      {/* A4 Report */}
      <div
        ref={printRef}
        style={{
          width: '210mm',
          minHeight: '297mm',
          margin: '20px auto',
          background: '#fff',
          padding: '15mm',
          fontFamily: 'system-ui, -apple-system, Arial, sans-serif',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
        className="print:m-0 print:shadow-none"
      >
        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #333', paddingBottom: '20px', marginBottom: '25px' }}>
          <div>
            {logoUrl ? (
              <img src={logoUrl} alt="Lab Logo" style={{ height: '80px', maxWidth: '200px', objectFit: 'contain' }} />
            ) : (
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e', padding: '10px 0' }}>{labName}</div>
            )}
          </div>
          <div style={{ textAlign: 'right', direction: 'rtl' }}>
            <p style={{ margin: '3px 0', fontSize: '13px' }}><strong>رقم الحجز:</strong> {booking?.booking_no || '-'}</p>
            <p style={{ margin: '3px 0', fontSize: '13px' }}><strong>تاريخ الجمع:</strong> {formatDate(booking?.booking_date)}</p>
            <p style={{ margin: '3px 0', fontSize: '13px' }}><strong>تاريخ الطباعة:</strong> {currentDate}</p>
          </div>
        </div>

        {/* ── Patient Info ── */}
        <div style={{ background: '#f8f9fa', padding: '15px 20px', borderRadius: '8px', marginBottom: '25px', border: '1px solid #e9ecef', direction: 'rtl' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
            <div><strong>اسم المريض:</strong> {customer?.full_name || '-'}</div>
            <div style={{ textAlign: 'left' }}><strong>رقم الهوية:</strong> {customer?.national_id || '-'}</div>
            <div><strong>العمر:</strong> {patientAge ? `${patientAge} سنة` : '-'}</div>
            <div style={{ textAlign: 'left' }}><strong>الجنس:</strong> {patientGender}</div>
            <div style={{ gridColumn: '1 / -1' }}><strong>المركز:</strong> {booking?.branch_id?.branch_name || '-'}</div>
          </div>
        </div>

        {/* ── Grouped Standard / Serial / Culture / Rich Text Tests ── */}
        {Object.entries(groupedTests).map(([category, categoryTests]) => (
          <div key={category} style={{ marginBottom: '30px', pageBreakInside: 'avoid' }}>
            {/* Category Header */}
            <div style={{
              background: '#1a1a2e', color: '#fff', padding: '12px 20px',
              fontWeight: 'bold', fontSize: '15px', textAlign: 'left', direction: 'ltr'
            }}>
              {category}
            </div>

            {/* Results Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', direction: 'ltr' }}>
              <thead>
                <tr style={{ background: '#f1f3f4' }}>
                  <th style={{ padding: '12px 15px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600', width: '30%' }}>Test Name</th>
                  <th style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '2px solid #ddd', fontWeight: '600', width: '25%' }}>Result</th>
                  <th style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '2px solid #ddd', fontWeight: '600', width: '15%' }}>Unit</th>
                  <th style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '2px solid #ddd', fontWeight: '600', width: '30%' }}>Reference Range</th>
                </tr>
              </thead>
              <tbody>
                {categoryTests.map((test, idx) => {
                  const testName = test.lab_test_id?.name_en || '-';
                  return (
                    <React.Fragment key={idx}>
                      {/* Test Name Header Row */}
                      <tr style={{ background: '#f8f9fa' }}>
                        <td colSpan="4" style={{ padding: '8px 15px', borderBottom: '1px solid #dee2e6', fontWeight: 'bold', fontSize: '14px', color: '#1a1a2e' }}>
                          {testName}
                        </td>
                      </tr>
                      {/* Test Content */}
                      {renderTestContent(test, idx)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        {/* ── Serial With Chart Tests (standalone sections) ── */}
        {serialChartTests.map((test, idx) => renderSerialWithChart(test, idx))}

        {/* ── Result Comment ── */}
        {tests?.some(t => t.result?.result_comment) && (
          <div style={{ margin: '20px 0', padding: '12px 16px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px', direction: 'rtl' }}>
            <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '6px', color: '#374151' }}>ملاحظات:</div>
            {tests.filter(t => t.result?.result_comment).map((t, i) => (
              <p key={i} style={{ fontSize: '12px', color: '#4b5563', margin: '4px 0' }}>
                <strong>{t.lab_test_id?.name_en || '-'}:</strong> {t.result.result_comment}
              </p>
            ))}
          </div>
        )}

        {/* ── Footer ── */}
        <div style={{ marginTop: 'auto', paddingTop: '30px', borderTop: '1px solid #ddd', textAlign: 'center', fontSize: '11px', color: '#888' }}>
          <p style={{ margin: '5px 0' }}>هذا التقرير صادر إلكترونياً من نظام إدارة المختبرات</p>
          <p style={{ margin: '5px 0' }}>This report is electronically generated</p>
        </div>
      </div>

      {/* ── Print Styles ── */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            margin: 0;
            background: #fff !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:m-0 { margin: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          @page { size: A4; margin: 0; }
          .recharts-wrapper, .recharts-surface { overflow: visible !important; }
          svg.recharts-surface { page-break-inside: avoid; }
          table { page-break-inside: avoid; }
          div[style*="pageBreakInside"] { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

// ─── Shared inline style objects ───

const styles = {
  cellLeft: {
    padding: '10px 15px',
    paddingLeft: '30px',
    borderBottom: '1px solid #eee',
    fontWeight: '500',
    color: '#444'
  },
  cellCenter: {
    padding: '10px 15px',
    textAlign: 'center',
    borderBottom: '1px solid #eee'
  }
};

export default MedicalReport;
